import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../utils/api";
import { ApiResponse } from "../types/api";
import { FireSimulationGroup } from "../types/FireSimulationGroup";

export const useSimulationGroups = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["simulationGroups", projectId],
    queryFn: () =>
      api.request<ApiResponse<FireSimulationGroup[]>>(
        `/api/projects/${projectId}/fire-simulation-groups/`
      ),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useCreateSimulationGroup = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await api.request<ApiResponse<FireSimulationGroup>>(
        `/api/projects/${projectId}/fire-simulation-groups/`,
        { method: "POST", body: { name } }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulationGroups", projectId] });
      queryClient.invalidateQueries({ queryKey: ["simulations", projectId] });
    },
  });
};

export const useRenameSimulationGroup = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, name }: { groupId: number; name: string }) => {
      const res = await api.request<ApiResponse<FireSimulationGroup>>(
        `/api/projects/${projectId}/fire-simulation-groups/${groupId}`,
        { method: "PATCH", body: { name } }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulationGroups", projectId] });
    },
  });
};

export const useDeleteSimulationGroup = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: number) => {
      await api.request<void>(
        `/api/projects/${projectId}/fire-simulation-groups/${groupId}`,
        { method: "DELETE" }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulationGroups", projectId] });
      queryClient.invalidateQueries({ queryKey: ["simulations", projectId] });
    },
  });
};

export const useUpdateSimulationGroup = () => {
  // PATCH simulation's group field via existing endpoint
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ simulationId, group }: { simulationId: string; group: number | null }) => {
      const res = await api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/fire-simulations/${simulationId}`,
        { method: "PATCH", body: { group } }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulations", projectId] });
    },
  });
};
